import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import cookie from "react-cookies";
import {
  completeIntroUpload,
  getMyCreatorIntro,
  putFileWithProgress,
  requestIntroUploadUrl,
  submitCreatorIntro,
} from "../../../services/creatorApi";
import { TrainingLoading } from "../../academy/AcademyShared";
import { CreatorLayout, CREATOR_CRUMBS, CreatorStatusChip } from "./CreatorShared";
import { handleCreatorGateError } from "./creatorLicense";

const MAX_BYTES = 200 * 1024 * 1024;

function IntroGuide({ brief, onOpenImage }) {
  const [copiedIndex, setCopiedIndex] = useState(null);
  const examples = brief?.example_images || [];
  const structure = brief?.structure || [];
  const options = brief?.spoken_options || [];

  const copyLine = async (line, index) => {
    try {
      await navigator.clipboard.writeText(line);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex((current) => (current === index ? null : current)), 1800);
    } catch {
      setCopiedIndex(null);
    }
  };

  return (
    <div className="creator-intro-guide">
      {examples.length > 0 ? (
        <div className="creator-brief-previews">
          <p className="creator-brief-say-label">How it should look</p>
          <div className="creator-brief-preview-grid">
            {examples.map((example, index) => (
              <button
                key={example.url}
                type="button"
                className="creator-brief-preview"
                onClick={() => onOpenImage({ src: example.url, label: example.caption })}
              >
                <img
                  src={example.url}
                  alt={example.caption || `Intro example ${index + 1}`}
                  loading="lazy"
                />
                <span className="creator-brief-preview__cap">{example.caption}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {structure.length > 0 ? (
        <div className="creator-intro-structure">
          <p className="creator-brief-say-label">What to say, in order</p>
          <ol className="creator-intro-structure__list">
            {structure.map((step) => (
              <li key={step.label}>
                <strong>{step.label}</strong>
                {step.hint ? <span>{step.hint}</span> : null}
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      {options.length > 0 ? (
        <div className="creator-intro-options">
          <p className="creator-brief-say-label">Example lines</p>
          <p className="creator-intro-options__hint">
            Pick one style and swap in your own name, place and story. Don&apos;t read it word for word.
          </p>
          {options.map((option, index) => (
            <div key={option.label} className="creator-say-panel creator-intro-option">
              <div className="creator-intro-option__head">
                <span className="creator-chip creator-chip--orange">{option.label}</span>
                <button
                  type="button"
                  className="creator-intro-option__copy"
                  onClick={() => copyLine(option.line, index)}
                >
                  {copiedIndex === index ? "Copied" : "Copy"}
                </button>
              </div>
              <p className="creator-intro-option__line">&ldquo;{option.line}&rdquo;</p>
            </div>
          ))}
        </div>
      ) : null}

      {brief?.do_this || brief?.avoid_this ? (
        <div className="creator-v2-do-avoid">
          {brief.do_this ? (
            <div className="creator-brief-callout creator-brief-callout--do">
              <strong>Do this</strong>
              <p>{brief.do_this}</p>
            </div>
          ) : null}
          {brief.avoid_this ? (
            <div className="creator-brief-callout creator-brief-callout--avoid">
              <strong>Avoid this</strong>
              <p>{brief.avoid_this}</p>
            </div>
          ) : null}
        </div>
      ) : null}

      {brief?.checklist?.length ? (
        <div className="creator-intro-checklist">
          <p className="creator-brief-say-label">Before you upload</p>
          <ul>
            {brief.checklist.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function introChipStatus(status) {
  if (status === "submitted") return "submitted";
  if (status === "retake") return "retake";
  if (status === "approved") return "approved";
  if (status === "rejected") return "rejected";
  if (status === "draft") return "in_progress";
  return "available";
}

export default function CreatorIntro() {
  const [intro, setIntro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [local, setLocal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const cancelRef = useRef(null);
  const previewRef = useRef(null);

  const load = useCallback(async () => {
    const res = await getMyCreatorIntro();
    setIntro(res.intro);
    return res.intro;
  }, []);

  useEffect(() => {
    if (!cookie.load("candidateKey")) {
      window.location.href = "/login?next=/me/creator/intro";
      return;
    }
    setLoading(true);
    load()
      .catch((e) => setError(e.message || "Could not load intro"))
      .finally(() => setLoading(false));
  }, [load]);

  useEffect(() => {
    return () => {
      cancelRef.current?.();
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    };
  }, []);

  const onPickFile = async (file) => {
    if (!file) return;
    if (
      intro?.has_file &&
      !window.confirm("Replace your current intro with this new file?")
    ) {
      return;
    }
    setError("");
    setMessage("");

    if (file.size > MAX_BYTES) {
      setError("Keep the intro under 200 MB. Aim for 20 to 30 seconds.");
      return;
    }

    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    const previewUrl = URL.createObjectURL(file);
    previewRef.current = previewUrl;
    setLocal({ phase: "uploading", progress: 0, previewUrl });

    try {
      const prepared = await requestIntroUploadUrl({
        name: file.name,
        type: file.type,
        size: file.size,
      });
      const { promise, cancel } = putFileWithProgress(prepared.upload_url, file, {
        contentType: prepared.content_type || file.type,
        onProgress: (progress) =>
          setLocal((prev) => ({ ...prev, phase: "uploading", progress, previewUrl })),
      });
      cancelRef.current = cancel;
      await promise;
      cancelRef.current = null;

      const saved = await completeIntroUpload({
        s3_key: prepared.s3_key,
        content_type: prepared.content_type || file.type,
        byte_size: file.size,
        file_name: file.name,
      });
      setIntro(saved.intro);
      setLocal({ phase: "uploaded", previewUrl });
      setMessage("Intro uploaded. Submit it for review when you are happy with it.");
    } catch (e) {
      if (handleCreatorGateError(e, "/me/creator/intro")) return;
      setLocal({ phase: "error", previewUrl, note: e.message });
      setError(e.message || "Upload failed");
    }
  };

  const onSubmit = async () => {
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      const res = await submitCreatorIntro();
      setIntro(res.intro);
      setMessage(res.message || "Intro submitted for review.");
    } catch (e) {
      if (handleCreatorGateError(e, "/me/creator/intro")) return;
      setError(e.message || "Could not submit intro");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <CreatorLayout breadcrumbs={CREATOR_CRUMBS.intro} backTo="/me/creator">
        <TrainingLoading label="Loading intro..." />
      </CreatorLayout>
    );
  }

  const status = intro?.status || "none";
  const previewSrc = local?.previewUrl || intro?.play_url || null;
  const canUpload = intro?.can_upload !== false && status !== "submitted" && status !== "approved";
  const canSubmit = Boolean(intro?.can_submit);

  return (
    <CreatorLayout breadcrumbs={CREATOR_CRUMBS.intro} backTo="/me/creator">
      <section className="creator-hero">
        <p className="training-kicker">Creator</p>
        <h2 className="creator-hero__title">Personal intro</h2>
        <p className="creator-hero__lede">
          Record a short unpaid intro so QikTruck can unlock paid scripts for you.
          You can still browse the catalogue while this is in review.
        </p>
      </section>

      <article className="training-card creator-script-card">
        <div className="creator-script-card__top">
          <div className="creator-script-card__identity">
            <span className="creator-script-card__code">INTRO</span>
            <CreatorStatusChip status={introChipStatus(status)} />
          </div>
          <span className="creator-script-card__pay">Unpaid</span>
        </div>

        <h3 className="creator-script-card__title">
          {intro?.brief?.title || "Personal intro"}
        </h3>
        <p className="creator-script-card__meta">
          {intro?.brief?.duration_hint || "20 to 30 seconds"} · vertical 9:16
        </p>
        <p className="creator-script-card__blurb">{intro?.brief?.spoken_prompt}</p>

        {!intro?.brief?.checklist?.length ? (
          <ul className="creator-how-banner__list" style={{ marginTop: 12 }}>
            {(intro?.brief?.filming_notes || []).map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        ) : null}

        {intro?.retake_note ? (
          <div className="creator-brief-callout creator-brief-callout--avoid" style={{ marginTop: 16 }}>
            <strong>What to fix</strong>
            <p>{intro.retake_note}</p>
          </div>
        ) : null}

        {status === "rejected" && !intro?.retake_note ? (
          <div className="creator-brief-callout creator-brief-callout--avoid" style={{ marginTop: 16 }}>
            <strong>We need a new intro</strong>
            <p>
              {intro?.admin_note ||
                "This intro did not pass review. That is okay. Upload a fresh clip and we will take another look."}
            </p>
          </div>
        ) : null}

        {status === "approved" ? (
          <div className="creator-brief-callout creator-brief-callout--do" style={{ marginTop: 16 }}>
            <strong>Approved</strong>
            <p>Paid scripts are unlocked. Browse the catalogue and start filming.</p>
          </div>
        ) : null}

        {status === "submitted" ? (
          <p className="creator-tip" style={{ marginTop: 16 }}>
            Thanks. Your intro is in review. You can browse scripts now. Filming unlocks after approval.
          </p>
        ) : null}

        {canUpload ? (
          <IntroGuide brief={intro?.brief} onOpenImage={setLightbox} />
        ) : null}

        {previewSrc ? (
          <div style={{ marginTop: 16 }}>
            <video
              key={previewSrc}
              src={previewSrc}
              controls
              playsInline
              style={{ width: "100%", maxHeight: 420, borderRadius: 12, background: "#111" }}
            />
          </div>
        ) : null}

        {local?.phase === "uploading" ? (
          <p className="creator-tip" style={{ marginTop: 12 }}>
            Uploading… {local.progress || 0}%
          </p>
        ) : null}

        {error ? <p className="creator-error">{error}</p> : null}
        {message ? <p className="creator-tip">{message}</p> : null}

        <div
          className={`creator-script-card__actions${
            canUpload && !canSubmit && status !== "approved"
              ? " creator-script-card__actions--single"
              : ""
          }`}
          style={{ marginTop: 16 }}
        >
          {canUpload ? (
            <label
              className="training-btn-primary creator-intro-primary-cta"
              style={{ cursor: "pointer" }}
            >
              {intro?.has_file || local?.previewUrl ? "Replace intro" : "Choose intro video"}
              <input
                type="file"
                accept="video/mp4,video/quicktime,video/webm,video/x-m4v"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  onPickFile(file);
                }}
              />
            </label>
          ) : null}

          {canSubmit ? (
            <button
              type="button"
              className="training-btn-primary creator-intro-primary-cta"
              disabled={submitting || local?.phase === "uploading"}
              onClick={onSubmit}
            >
              {submitting ? "Submitting..." : "Submit for review"}
            </button>
          ) : null}

          {status === "approved" ? (
            <Link className="training-btn-primary" to="/me/creator">
              Browse paid scripts
            </Link>
          ) : null}
        </div>
      </article>

      {lightbox ? (
        <div
          className="creator-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={lightbox.label || "Intro example"}
          onClick={() => setLightbox(null)}
        >
          <button type="button" className="creator-lightbox__close" onClick={() => setLightbox(null)}>
            Close
          </button>
          <img src={lightbox.src} alt={lightbox.label || "Intro example"} />
        </div>
      ) : null}
    </CreatorLayout>
  );
}
